import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AlertToast from './AlertToast';

const AlertToastContainer = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);

  // Chave do localStorage para alertas já exibidos nesta sessão
  const DISPLAYED_ALERTS_KEY = 'stakemind_displayed_alerts';

  // Obter IDs dos alertas já exibidos do localStorage
  const getDisplayedAlertIds = () => {
    try {
      const stored = localStorage.getItem(DISPLAYED_ALERTS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  // Salvar ID de alerta exibido no localStorage
  const markAlertAsDisplayed = (alertId) => {
    try {
      const displayed = getDisplayedAlertIds();
      if (!displayed.includes(alertId)) {
        displayed.push(alertId);
        localStorage.setItem(DISPLAYED_ALERTS_KEY, JSON.stringify(displayed));
      }
    } catch (error) {
      console.error('Erro ao salvar alerta exibido:', error);
    }
  };

  // Carregar alertas não lidos
  const loadAlerts = async () => {
    if (!user) {
      return;
    }

    try {
      // Gerar novos alertas
      const generateResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/generate?user_id=${user.id}`, {
        method: 'POST'
      });
      const generateData = await generateResponse.json();

      // Buscar alertas não lidos
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts?user_id=${user.id}&unread_only=true`);
      if (response.ok) {
        const data = await response.json();
        
        // Pegar IDs dos alertas já exibidos nesta sessão
        const displayedIds = getDisplayedAlertIds();
        
        // Filtrar apenas alertas que NUNCA foram exibidos nesta sessão
        const newAlerts = data.filter(alert => !displayedIds.includes(alert.id));
        
        if (newAlerts.length > 0) {
          console.log('🔔 Novos alertas para exibir:', newAlerts.length);
          
          // Adicionar aos alertas visíveis
          setAlerts(prev => {
            // Remover duplicatas (caso já estejam no state)
            const existingIds = prev.map(a => a.id);
            const filtered = newAlerts.filter(a => !existingIds.includes(a.id));
            return [...prev, ...filtered];
          });
          
          // Marcar como exibidos
          newAlerts.forEach(alert => markAlertAsDisplayed(alert.id));
        }
      }
    } catch (error) {
      console.error('❌ Error loading alerts:', error);
    }
  };

  useEffect(() => {
    if (user) {
      // Carregar imediatamente apenas uma vez
      loadAlerts();
      // Verificar novos alertas a cada 1 minuto
      const interval = setInterval(loadAlerts, 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const removeAlert = (alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const markAsRead = async (alertId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/${alertId}/read`, {
        method: 'PUT'
      });
      removeAlert(alertId);
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  // Limpar alertas exibidos quando o usuário faz logout ou troca de conta
  useEffect(() => {
    if (!user) {
      // Limpar localStorage ao deslogar
      localStorage.removeItem(DISPLAYED_ALERTS_KEY);
    }
  }, [user]);

  // Limitar a 3 alertas visíveis por vez
  const visibleAlerts = alerts.slice(0, 3);

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes progressBar {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>

      <div className="fixed top-20 right-4 z-50 flex flex-col gap-3 pointer-events-none">
        <div className="flex flex-col gap-3 pointer-events-auto">
          {visibleAlerts.map((alert) => (
            <AlertToast
              key={alert.id}
              alert={alert}
              onClose={() => removeAlert(alert.id)}
              onMarkRead={markAsRead}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default AlertToastContainer;
